import { Controller } from '@hotwired/stimulus'
import { googleFontStack, loadGoogleFont } from '../embed/google_font'

// Builder theme panel: every control writes the same CSS variables EventFormTheme.css_variables
// emits straight onto the mounted preview, so a change shows before it is saved. Keep the
// variable map in step with app/models/event_form_theme.rb.
const FONTS = {
  inherit: 'inherit',
  system: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  serif: 'Georgia, "Times New Roman", serif'
}

// Mirrors EventFormTheme.field_text / luminance: field text must read on the field colour.
function luminance(hex) {
  const [red, green, blue] = hex.replace('#', '').match(/../g).map((pair) => {
    const channel = parseInt(pair, 16) / 255
    return channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4
  })
  return 0.2126 * red + 0.7152 * green + 0.0722 * blue
}

function contrast(one, two) {
  const [light, dark] = [luminance(one), luminance(two)].sort((a, b) => b - a)
  return (light + 0.05) / (dark + 0.05)
}

function fieldText(theme) {
  const field = theme.input_background
  if (contrast(theme.text, field) >= 4.5) return theme.text
  return contrast('#111111', field) >= contrast('#ffffff', field) ? '#111111' : '#ffffff'
}

export function variables(theme) {
  const solid = theme.button_style !== 'outline'
  return {
    '--ef-bg': theme.background, '--ef-text': theme.text, '--ef-accent': theme.accent,
    '--ef-button-text': theme.button_text, '--ef-input-bg': theme.input_background, '--ef-border': theme.border,
    '--ef-radius-button': theme.button_radius === 'pill' ? '9999px' : `${theme.button_radius}px`,
    '--ef-radius-field': `${theme.field_radius}px`,
    '--ef-font': theme.font === 'google' ? googleFontStack(theme.font_family, theme.font_category) : FONTS[theme.font] || 'inherit',
    '--ef-button-width': theme.button_width === 'auto' ? 'auto' : '100%',
    '--ef-button-bg': solid ? theme.accent : 'transparent',
    '--ef-button-fg': solid ? theme.button_text : theme.accent,
    // Mirrors EventFormTheme.css_variables: an outline button fills on hover and drops the colored glow.
    '--ef-button-hover-bg': theme.accent,
    '--ef-button-hover-fg': theme.button_text,
    '--ef-button-shadow': solid ? `0 1px 2px rgba(0,0,0,.06), 0 8px 20px -10px ${theme.accent}b3` : 'none',
    '--ef-button-shadow-hover': solid ? `0 2px 4px rgba(0,0,0,.06), 0 12px 24px -10px ${theme.accent}bf` : 'none',
    '--ef-input-text': fieldText(theme),
    '--ef-inset': luminance(theme.background) < 0.9 ? '1.25rem' : '0px'
  }
}

export default class extends Controller {
  static targets = ['input', 'buttonRadius', 'buttonRadiusLabel', 'pill', 'fieldRadiusLabel', 'fontFamily']
  static values = { host: String }

  connect() {
    this.update()
    this.loadFont()
  }

  // A segmented control: one hidden input per key, the pressed pill marks the value.
  choose(event) {
    const button = event.currentTarget
    const { themeKey, value } = button.dataset
    this.input(themeKey).value = value
    this.update()
    if (themeKey === 'font' && value === 'google') {
      this.loadFont()
      this.input('font_family').focus()
    }
  }

  preset(event) {
    const theme = JSON.parse(event.currentTarget.dataset.theme)
    for (const [key, value] of Object.entries(theme)) {
      const input = this.input(key)
      if (input) input.value = value
    }
    this.pillTarget.checked = theme.button_radius === 'pill'
    this.buttonRadiusTarget.value = theme.button_radius === 'pill' ? 48 : theme.button_radius
    this.update()
  }

  // Every path that can change a pill's value (a click, a preset, "Match my Smart menu" filling the
  // hidden font input directly) re-syncs every segmented control here, so none of them can go stale.
  update() {
    const pill = this.pillTarget.checked
    this.buttonRadiusTarget.disabled = pill
    this.input('button_radius').value = pill ? 'pill' : this.buttonRadiusTarget.value
    this.buttonRadiusLabelTarget.textContent = pill ? 'Pill' : `${this.buttonRadiusTarget.value}px`
    this.fieldRadiusLabelTarget.textContent = `${this.input('field_radius').value}px`
    this.fontFamilyTarget.hidden = this.input('font').value !== 'google'

    const theme = this.theme()
    this.element.querySelectorAll('.nav-link[data-theme-key]').forEach((link) => {
      const active = String(theme[link.dataset.themeKey]) === link.dataset.value
      link.classList.toggle('active', active)
      link.setAttribute('aria-pressed', active)
    })

    // Every match: the preview modal's host and, while one is open, the on-page launcher preview.
    const cssVariables = variables(theme)
    document.querySelectorAll(this.hostValue).forEach((host) => {
      for (const [name, value] of Object.entries(cssVariables)) host.style.setProperty(name, value)
      // The renderer decides the inner padding against what is behind the form (embed/form.js updateInset).
      host.dispatchEvent(new CustomEvent('eightyeight:theme'))
    })
  }

  // On change, not input: every keystroke would request a partial family name ("Lo") and leave a dead <link>.
  loadFont() {
    if (this.input('font').value === 'google') loadGoogleFont(this.input('font_family').value)
  }

  theme() {
    const theme = {}
    this.inputTargets.forEach((input) => { theme[input.dataset.themeKey] = input.value })
    return theme
  }

  input(key) {
    return this.inputTargets.find((input) => input.dataset.themeKey === key)
  }
}
